export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
  FREE_DAYS = 'free_days'
}

export enum DiscountStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  EXPIRED = 'expired',
  USED_UP = 'used_up'
}

export enum ApplicableVehicleType {
  ALL = 'all',
  ECONOMY = 'economy',
  COMPACT = 'compact',
  MIDSIZE = 'midsize',
  FULLSIZE = 'fullsize',
  LUXURY = 'luxury',
  SUV = 'suv',
  SPECIFIC_VEHICLES = 'specific_vehicles'
}

export interface UsageRecord {
  bookingId: string;
  customerId: string;
  usedDate: Date;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
}

export interface ModificationRecord {
  date: Date;
  field: string;
  oldValue: string;
  newValue: string;
  modifiedBy: string;
  reason?: string;
}

export interface Discount {
  _id: string;
  code: string;
  name: string;
  description?: string;
  type: DiscountType;
  value: number;
  status: DiscountStatus;
  validFrom: Date;
  validUntil: Date;
  usageLimit: number;
  usageCount: number;
  usagePerCustomer: number;
  minimumRentalDays: number;
  maximumRentalDays: number;
  minimumAmount: number;
  maximumDiscountAmount: number;
  applicableVehicleType: ApplicableVehicleType;
  specificVehicles: string[];
  applicableLocations: string[];
  excludedLocations: string[];
  isFirstTimeCustomerOnly: boolean;
  isRecurringCustomerOnly: boolean;
  applicableDaysOfWeek: string[];
  blackoutDates: Date[];
  canCombineWithOtherDiscounts: boolean;
  createdBy?: string;
  lastModifiedBy?: string;
  usageHistory: UsageRecord[];
  isAutoApply: boolean;
  priority: number;
  internalNotes?: string;
  isPublic: boolean;
  marketingCampaign?: string;
  budgetLimit: number;
  budgetUsed: number;
  modificationHistory: ModificationRecord[];
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateDiscountDto {
  code: string;
  name: string;
  description?: string;
  type: DiscountType;
  value: number;
  validFrom: string;
  validUntil: string;
  usageLimit?: number;
  usagePerCustomer?: number;
  minimumRentalDays?: number;
  maximumRentalDays?: number;
  minimumAmount?: number;
  maximumDiscountAmount?: number;
  applicableVehicleType?: ApplicableVehicleType;
  specificVehicles?: string[];
  applicableLocations?: string[];
  excludedLocations?: string[];
  isFirstTimeCustomerOnly?: boolean;
  isRecurringCustomerOnly?: boolean;
  applicableDaysOfWeek?: string[];
  blackoutDates?: string[];
  canCombineWithOtherDiscounts?: boolean;
  createdBy?: string;
  isAutoApply?: boolean;
  priority?: number;
  internalNotes?: string;
  isPublic?: boolean;
  marketingCampaign?: string;
  budgetLimit?: number;
}

export interface UpdateDiscountDto extends Partial<CreateDiscountDto> {}

export interface DiscountFilters {
  status?: DiscountStatus;
  type?: DiscountType;
  isPublic?: boolean;
  isActive?: boolean;
  search?: string;
}

export interface DiscountUsageStats {
  totalUsage: number;
  totalDiscountAmount: number;
  totalRevenueLoss: number;
  usageByMonth: {
    _id: { year: number; month: number };
    count: number;
    totalDiscount: number;
  }[];
  topCustomers: {
    _id: string;
    usageCount: number;
    totalSavings: number;
  }[];
}

export interface DiscountValidationResult {
  isValid: boolean;
  discount?: Discount;
  discountAmount?: number;
  finalAmount?: number;
  errors?: string[];
}

export interface ApplyDiscountDto {
  discountCode: string;
  customerId: string;
  carId: string;
  originalAmount: number;
  rentalDays: number;
  pickUpDate: string;
  returnDate: string;
  pickUpLocation: string;
  bookingId?: string;
}